import React, { useMemo } from 'react'
import Group from 'components/utils/Group'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import TextButton from 'components/Luxkit/Button/TextButton'

import { formatDateRange, getSearchQueryFromFilter } from 'lib/search/searchUtils'
import styled from 'styled-components'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useDestinationMaxSavings } from 'home/hooks/useDestinationMaxSavings'
import HotelDestinationTile from 'components/Pages/HotelSearchPage/HotelDestinationTile'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import HotelSearchCarouselLoadingTile from 'components/Pages/HotelSearchPage/HotelSearchCarouselLoadingTile'
import useDestinationOfferList from 'hooks/Offers/useDestinationOfferList'

const Container = styled(Group)`
  ${mediaQueryDown.mobile} {
    padding: ${rem(0)} ${rem(20)};
    background-color: transparent;
  }
`

const ViewMoreButton = styled(TextButton)`
  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const StyledHeader = styled.div`
  font-size: ${rem(16)};

  ${mediaQueryDown.mobile} {
    font-size: ${rem(14)};
  }
`

interface Props {
  filters: App.OfferListFilters;
  destinations: Array<App.DestinationInfo>;
}

function HotelSearchDestinationCarousel({
  filters,
  destinations,
}: Props) {
  const [offerLists, flashOfferLists] = useDestinationOfferList(destinations, filters)
  const [maxSavings, fetchingMaxSavings] = useDestinationMaxSavings(destinations.map(destination => destination.destinationId), 'hotel')

  const viewAllUrl = useMemo(() => {
    return `/search?${getSearchQueryFromFilter({
      ...filters,
      destinationName: ANYWHERE_NAME,
      destinationId: ANYWHERE_PLACE_ID,
      locations: destinations.map(destination => destination.name),
    })}`
  }, [destinations, filters])

  const displayDestinations = useMemo(() => {
    const unsortedDestinations = destinations.map((destination: App.DestinationInfo, index: number) => ({
      ...destination,
      offersCount: offerLists[index]?.offerCount,
      flashOfferCount: flashOfferLists[index]?.offerCount,
      fetching: offerLists[index]?.fetching || flashOfferLists[index]?.fetching,
      url: `/search?${getSearchQueryFromFilter({
        ...filters,
        destinationId: destination.destinationId,
        destinationName: destination.name,
      })}`,
    }))

    return {
      destinations: unsortedDestinations
        .filter(destination => destination.offersCount > 0)
        // Sort by destination's flash offer count
        .sort((a, b) => b.flashOfferCount - a.flashOfferCount),
      anyFetching: unsortedDestinations.some(destination => destination.fetching),
    }
  }, [destinations, filters, flashOfferLists, offerLists])

  return (
    <Container direction="vertical" gap={8}>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <StyledHeader>
          {(filters.checkIn && filters.checkOut) ?
            `Similar beach destinations with availability ${formatDateRange(filters.checkIn, filters.checkOut)}` :
            'Explore more beach escapes in similar destinations'
         }
        </StyledHeader>
        <ViewMoreButton kind="tertiary" to={viewAllUrl}>
          View All
        </ViewMoreButton>
      </Group>
      <CardCarousel gap={12}>
        {!displayDestinations.anyFetching && displayDestinations.destinations.map((destination: App.DestinationInfo) => (
          <HotelDestinationTile
            key={destination.destinationId}
            destination={destination}
            fetchingMaxSavings={fetchingMaxSavings}
            savings={maxSavings[destination.destinationId ?? '']}
          />
        ))}
        {displayDestinations.anyFetching &&
          Array.from({ length: 6 }).map((_, index) => (
            <HotelSearchCarouselLoadingTile key={index} />
          ))
        }
      </CardCarousel>
    </Container>
  )
}

export default HotelSearchDestinationCarousel
